import { inject, Injectable } from '@angular/core';
import { JeevesInvoiceListConfigService } from 'gung-standard-jeeves';
import { AuthService, Invoice, MetadataService } from 'gung-standard';
import { first, forkJoin, map, Observable } from 'rxjs';
import { HlDisplayInvoiceCardListComponent } from '../components/hl-display-invoice-card-list/hl-display-invoice-card-list.component';
import { ConfigBaseFilter, ListLayout, ListLayoutSingleComponent } from 'gung-list';
import { HlDisplayPortalFeaturesService, OrderSearchSettingsGroup } from './hl-display-portal-features.service';
import { HlSalesPersonFilter } from './filters/hl-display-sales-filters';

interface InvoicesFilterGating {
  salesPersonFilterEnabled: boolean;
  isSalesOrCustomerService: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class HlDisplayInvoicesListConfigService extends JeevesInvoiceListConfigService {
  topFilter: boolean = false;

  protected metadataService = inject(MetadataService);
  protected hlDisplayPortalFeaturesService = inject(HlDisplayPortalFeaturesService);
  protected authService = inject(AuthService);

  protected salesPersonFilter = new HlSalesPersonFilter<Invoice>(this.metadataService, item => item.extra.fh?.saljare);

  protected filterGating?: InvoicesFilterGating;

  getItems(): Observable<Invoice[]> {
    return forkJoin({
      invoices: this.invoiceService.getOpenInvoices().pipe(first()),
      settings: this.hlDisplayPortalFeaturesService.getPortalFeatureGroup('orderSearchSettingsGroup').pipe(first()),
      roles: this.authService.getRoles().pipe(first())
    }).pipe(
      map(({ invoices, settings, roles }) => {
        const orderSearchSettings = settings as OrderSearchSettingsGroup;
        this.filterGating = {
          // Defaults true on HLDisplayOrderSearchSettings; treat an absent field as enabled so a
          // frontend-before-backend deploy does not silently drop the filter.
          salesPersonFilterEnabled: orderSearchSettings.salesPersonFilterEnabledInvoice !== false,
          isSalesOrCustomerService: roles.includes('SALES') || roles.includes('CUSTOMER_SERVICE')
        };
        return invoices;
      }),
      map(results =>
        results.sort((a, b) => {
          return a.extra.fh.faktdat < b.extra.fh.faktdat ? 1 : -1;
        })
      ),
      map(results => {
        // filter status
        const filterStatus = results.filter(result => result.extra.fh.faktstkod === 0);
        // filter type
        const filtertype = filterStatus.filter(result => result.extra.fh.facturatyp !== 120);

        return filtertype;
      })
    );
  }

  getLayouts(): ListLayout<Invoice>[] {
    return [
      {
        getIconClass: () => '',
        getListItemComponent: () => HlDisplayInvoiceCardListComponent,
        getListLayoutComponent: () => ListLayoutSingleComponent,
        getName: () => 'Invoice grid'
      }
    ];
  }

  getFilters(): ConfigBaseFilter<Invoice>[] {
    const filters = super.getFilters();

    const gating = this.filterGating;
    if (!gating) {
      // Unreachable via the Invoices tab itself - see filterGating. Kept loud rather than silent
      // because an unresolved role check must never be read as "the user has the role": that
      // would expose internal rep assignment to a customer-facing portal user.
      console.warn(
        'HlDisplayInvoicesListConfigService.getFilters() ran before getItems() resolved the portal settings and user roles; rendering no portal-gated filters.'
      );
      return filters;
    }

    if (gating.salesPersonFilterEnabled && gating.isSalesOrCustomerService) {
      filters.push(this.salesPersonFilter);
    }

    return filters;
  }
}
